// School roster + per-user print permission (org admin side).
// can_print default-allow: row absence = allowed. Toggling to "Approval
// required" is what routes a user's prints through the queue.
//
// V2.1: org admins also assign credits to a user from the school's pool
// (tier-sized; remaining shown in the header). Idempotent per assign intent.

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { orgApi } from '../../services/orgApi';
import { formatDate } from '../../i18n/formatters';
import { orgStyles } from './OrgConsole';
import LicenseStatCards from './LicenseStatCards';

function fmtWhen(iso) {
  if (!iso) return '—';
  return formatDate(iso);
}

function newIdempotencyKey() {
  try { return crypto.randomUUID(); }
  catch { return `assign-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

const inputStyle = {
  width: '72px', padding: '7px 10px', borderRadius: '8px',
  border: '1px solid rgba(27,37,80,0.142)', background: 'rgba(27,37,80,0.057)',
  color: 'rgba(27,37,80,0.855)', fontSize: '13px', textAlign: 'center',
};

export default function UsersTab() {
  const { t } = useTranslation('orgAdmin');
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;

  const [users, setUsers] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [prov, setProv] = useState(null);          // { seats, credits }
  const [licSummary, setLicSummary] = useState(null); // { total, available, inUse, revoked }
  const [loading, setLoading] = useState(true);
  const [busyUser, setBusyUser] = useState(null);
  // Roster filter. A school-linked user with no license (their key was reset or
  // revoked) is NOT currently on the system — hide them by default so the list
  // shows who can actually sign in today.
  const [userFilter, setUserFilter] = useState('current');

  // Credit-assignment inline panel state.
  const [assigningUser, setAssigningUser] = useState(null);
  const [assignAmount, setAssignAmount] = useState('');
  const [assignKey, setAssignKey] = useState(null);
  const [assignBusy, setAssignBusy] = useState(false);

  // Credit-reclaim inline panel state (pull credits back into the pool).
  const [reclaimingUser, setReclaimingUser] = useState(null);
  const [reclaimAmount, setReclaimAmount] = useState('');
  const [reclaimKey, setReclaimKey] = useState(null);
  const [reclaimBusy, setReclaimBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const [u, p, lic] = await Promise.allSettled([
      orgApi.listUsers(token),
      orgApi.getProvisioning(token),
      orgApi.listLicenses(token),
    ]);
    if (u.status === 'fulfilled') {
      setUsers(u.value?.users || []);
      setUnassigned(u.value?.unassigned || []);
    } else {
      toast.showError(t('users.toasts.loadUsersFailed', { error: u.reason?.message }));
    }
    setProv(p.status === 'fulfilled' ? (p.value || null) : null);
    setLicSummary(lic.status === 'fulfilled' ? (lic.value?.summary || null) : null);
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const creditsRemaining = prov?.credits?.remaining ?? 0;
  const canAssign = !!prov?.credits?.poolExists && creditsRemaining > 0;

  const openAssign = (u) => {
    setReclaimingUser(null);
    setAssigningUser(u.id);
    setAssignAmount('');
    setAssignKey(newIdempotencyKey());
  };

  const openReclaim = (u) => {
    setAssigningUser(null);
    setReclaimingUser(u.id);
    setReclaimAmount('');
    setReclaimKey(newIdempotencyKey());
  };

  const handleToggle = async (target) => {
    if (!token || busyUser) return;
    setBusyUser(target.id);
    try {
      const next = !(target.can_print !== false);
      await orgApi.setPrintPermission(token, target.id, next);
      refresh();
    } catch (e) {
      toast.showError(t('users.toasts.updateFailed', { error: e.message }));
    } finally {
      setBusyUser(null);
    }
  };

  const handleAssign = async (u) => {
    if (!token || assignBusy) return;
    const amt = parseInt(assignAmount, 10);
    if (!Number.isInteger(amt) || amt <= 0) { toast.showError(t('users.toasts.amountMin')); return; }
    if (prov && amt > creditsRemaining) { toast.showError(t('users.toasts.onlyRemaining', { count: creditsRemaining })); return; }
    setAssignBusy(true);
    try {
      const res = await orgApi.assignCredits(token, u.id, amt, assignKey);
      if (res?.alreadyApplied) {
        toast.showSuccess(t('users.toasts.alreadyApplied'), 3000);
      } else {
        toast.showSuccess(t('users.toasts.assigned', { count: amt, name: u.display_name || u.email }), 4000);
      }
      setAssigningUser(null);
      refresh();
    } catch (e) {
      const rem = e.body?.remaining;
      toast.showError(rem != null ? t('users.toasts.errorRemaining', { error: e.message, remaining: rem }) : t('users.toasts.assignFailed', { error: e.message }));
    } finally {
      setAssignBusy(false);
    }
  };

  const handleReclaim = async (u) => {
    if (!token || reclaimBusy) return;
    const amt = parseInt(reclaimAmount, 10);
    const balance = u.credits_balance ?? 0;
    if (!Number.isInteger(amt) || amt <= 0) { toast.showError(t('users.toasts.amountMin')); return; }
    if (amt > balance) { toast.showError(t('users.toasts.userOnlyHas', { name: u.display_name || u.email, count: balance })); return; }
    setReclaimBusy(true);
    try {
      const res = await orgApi.reclaimCredits(token, u.id, amt, reclaimKey);
      if (res?.alreadyApplied) {
        toast.showSuccess(t('users.toasts.alreadyApplied'), 3000);
      } else {
        toast.showSuccess(t('users.toasts.reclaimed', { count: amt, name: u.display_name || u.email }), 4000);
      }
      setReclaimingUser(null);
      refresh();
    } catch (e) {
      const rem = e.body?.remaining;
      toast.showError(rem != null ? t('users.toasts.errorMax', { error: e.message, max: rem }) : t('users.toasts.reclaimFailed', { error: e.message }));
    } finally {
      setReclaimBusy(false);
    }
  };

  const renderRow = (u, isUnassignedRow) => {
    const isAdminRow = u.is_org_admin === true;
    const canPrint = u.can_print !== false;
    const isSelf = u.id === user?.userId;
    const balance = u.credits_balance ?? 0;
    // Org admins manage every member's credits — including their own row and
    // co-admins. (Master admins never appear in this roster.)
    const showAssign = true;
    const showReclaim = balance > 0 && !!prov?.credits?.poolExists;
    return (
      <React.Fragment key={u.id}>
        <div style={{ ...orgStyles.card, display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...orgStyles.text, fontWeight: 600, fontSize: '14px' }}>
              {u.display_name || u.email}
              {isAdminRow && <span style={{ color: '#7F4BF6', fontSize: '11px', fontWeight: 700, marginLeft: '8px' }}>{t('users.badges.orgAdmin')}</span>}
              {isSelf && <span style={{ ...orgStyles.muted, fontSize: '11px', marginLeft: '8px' }}>{t('users.badges.you')}</span>}
              {isUnassignedRow && <span style={{ color: '#7F4BF6', fontSize: '11px', fontWeight: 700, marginLeft: '8px' }}>{t('users.badges.unassigned')}</span>}
              {!isUnassignedRow && !u.license_key && (
                <span
                  style={{ color: '#9B6DFF', fontSize: '11px', fontWeight: 700, marginLeft: '8px' }}
                  title={t('users.badges.licenseResetTitle')}
                >
                  {t('users.badges.licenseReset')}
                </span>
              )}
            </div>
            <div style={{ ...orgStyles.muted, fontSize: '12px', marginTop: '2px' }}>
              {u.email}
              {u.user_role ? ` · ${u.user_role}` : ''}
              {u.license_key ? ` · ${t('users.row.key', { key: u.license_key })}` : ` · ${t('users.row.noLicense')}`}
            </div>
          </div>

          {!isUnassignedRow && (
            <div style={{ ...orgStyles.muted, fontSize: '12px', textAlign: 'right', flexShrink: 0 }}>
              <div>{t('users.row.printsSummary', { total: u.total_prints ?? 0, month: u.prints_this_month ?? 0 })}</div>
              <div>{t('users.row.lastPrint', { when: fmtWhen(u.last_print_at) })}</div>
            </div>
          )}

          {/* Per-user credit balance */}
          <div style={{ ...orgStyles.muted, fontSize: '11px', textAlign: 'right', flexShrink: 0, minWidth: '58px' }}>
            <div style={{ ...orgStyles.text, fontWeight: 700, fontSize: '15px' }}>{balance}</div>
            <div style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('users.row.creditsLabel')}</div>
          </div>

          {/* Actions: assign / reclaim credits (all members incl. self) + print-permission toggle */}
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
            {showAssign && (
              <button
                style={{ ...orgStyles.btn, opacity: (canAssign && assigningUser !== u.id) ? 1 : 0.5 }}
                disabled={!canAssign || assigningUser === u.id}
                title={canAssign ? t('users.row.assignTitle') : t('users.row.assignTitleNoCredits')}
                onClick={() => openAssign(u)}
              >
                {t('users.row.assignCredits')}
              </button>
            )}
            {showReclaim && (
              <button
                style={{ ...orgStyles.btn, opacity: reclaimingUser === u.id ? 0.5 : 1 }}
                disabled={reclaimingUser === u.id}
                title={t('users.row.reclaimTitle')}
                onClick={() => openReclaim(u)}
              >
                {t('users.row.reclaimCredits')}
              </button>
            )}
            <div style={{ width: '150px', textAlign: 'right' }}>
              {isAdminRow ? (
                <span style={{ ...orgStyles.muted, fontSize: '12px' }}>{t('users.row.alwaysPrints')}</span>
              ) : (
                <button
                  style={{
                    ...orgStyles.btn,
                    background: canPrint ? 'rgba(38,199,116,0.12)' : 'rgba(127,75,246,0.12)',
                    borderColor: canPrint ? 'rgba(38,199,116,0.4)' : 'rgba(127,75,246,0.4)',
                    color: canPrint ? '#189A57' : '#9B6DFF',
                  }}
                  disabled={busyUser === u.id}
                  title={canPrint ? t('users.row.requireApprovalTitle') : t('users.row.allowDirectTitle')}
                  onClick={() => handleToggle(u)}
                >
                  {busyUser === u.id ? '…' : (canPrint ? t('users.row.printsDirectly') : t('users.row.approvalRequired'))}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Inline assign-credits panel */}
        {assigningUser === u.id && (
          <div style={{ ...orgStyles.card, borderColor: 'rgba(127,75,246,0.45)', background: 'rgba(127,75,246,0.07)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '12px 16px' }}>
            <span style={{ ...orgStyles.text, fontSize: '13px' }}>
              <Trans
                t={t}
                i18nKey="users.assignPanel.label"
                values={{ name: u.display_name || u.email }}
                components={{ bold: <b /> }}
              />
            </span>
            <input
              type="number" min="1" max={Math.max(creditsRemaining, 1)} value={assignAmount}
              onChange={(e) => { setAssignAmount(e.target.value); setAssignKey(newIdempotencyKey()); }}
              autoFocus
              style={inputStyle}
              aria-label={t('users.assignPanel.amountAria')}
            />
            <button style={{ ...orgStyles.btnPrimary, opacity: assignBusy ? 0.6 : 1 }} disabled={assignBusy} onClick={() => handleAssign(u)}>
              {assignBusy ? t('users.assignPanel.assigning') : t('users.assignPanel.assign')}
            </button>
            <button style={orgStyles.btn} disabled={assignBusy} onClick={() => setAssigningUser(null)}>{t('common:actions.cancel')}</button>
            <span style={{ ...orgStyles.muted, fontSize: '12px' }}>{t('users.assignPanel.remainingInPool', { remaining: creditsRemaining })}</span>
          </div>
        )}

        {/* Inline reclaim-credits panel */}
        {reclaimingUser === u.id && (
          <div style={{ ...orgStyles.card, borderColor: 'rgba(127,75,246,0.45)', background: 'rgba(127,75,246,0.07)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '12px 16px' }}>
            <span style={{ ...orgStyles.text, fontSize: '13px' }}>
              <Trans
                t={t}
                i18nKey="users.reclaimPanel.label"
                values={{ name: u.display_name || u.email }}
                components={{ bold: <b /> }}
              />
            </span>
            <input
              type="number" min="1" max={Math.max(balance, 1)} value={reclaimAmount}
              onChange={(e) => { setReclaimAmount(e.target.value); setReclaimKey(newIdempotencyKey()); }}
              autoFocus
              style={inputStyle}
              aria-label={t('users.reclaimPanel.amountAria')}
            />
            <button style={{ ...orgStyles.btnPrimary, opacity: reclaimBusy ? 0.6 : 1 }} disabled={reclaimBusy} onClick={() => handleReclaim(u)}>
              {reclaimBusy ? t('users.reclaimPanel.reclaiming') : t('users.reclaimPanel.reclaim')}
            </button>
            <button style={orgStyles.btn} disabled={reclaimBusy} onClick={() => setReclaimingUser(null)}>{t('common:actions.cancel')}</button>
            <span style={{ ...orgStyles.muted, fontSize: '12px' }}>{t('users.reclaimPanel.availableReturns', { balance })}</span>
          </div>
        )}
      </React.Fragment>
    );
  };

  // "Current" = holds a license right now (can sign in). "License reset" = still
  // linked to the school but their key was reset/revoked — kept for history,
  // credit reclaim, and so their settings survive a re-claim.
  const currentUsers = users.filter((u) => !!u.license_key);
  const resetUsers = users.filter((u) => !u.license_key);
  const visibleUsers = userFilter === 'current' ? currentUsers
    : userFilter === 'reset' ? resetUsers
    : users;
  const USER_FILTERS = [
    ['current', t('users.filters.current'), currentUsers.length],
    ['reset', t('users.filters.reset'), resetUsers.length],
    ['all', t('users.filters.all'), users.length],
  ];

  return (
    <div>
      {/* License pool stat cards — shared look with the Licenses tab */}
      <LicenseStatCards summary={licSummary} />

      {/* Credit pool header (V2.1) */}
      {prov && (
        <div style={{ ...orgStyles.card, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ ...orgStyles.muted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('users.creditPool.title')}</div>
            <div style={{ ...orgStyles.text, fontSize: '18px', fontWeight: 700, marginTop: '2px' }}>
              {t('users.creditPool.remaining', { remaining: prov.credits.remaining, allocated: prov.credits.allocated })}
              <span style={{ ...orgStyles.muted, fontWeight: 400, fontSize: '13px', marginLeft: '8px' }}>{t('users.creditPool.spent', { spent: prov.credits.spent })}</span>
            </div>
          </div>
          {!prov.credits.poolExists && (
            <div style={{ color: '#9B6DFF', fontSize: '12px' }}>{t('users.creditPool.notSetUp')}</div>
          )}
        </div>
      )}

      {/* Roster filter chips — default hides users whose license was reset */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {USER_FILTERS.map(([value, label, count]) => (
          <button
            key={value}
            onClick={() => setUserFilter(value)}
            style={{
              ...orgStyles.btn,
              padding: '5px 12px',
              fontSize: '12px',
              background: userFilter === value ? 'rgba(127,75,246,0.25)' : 'rgba(27,37,80,0.048)',
              borderColor: userFilter === value ? 'rgba(127,75,246,0.6)' : 'rgba(27,37,80,0.114)',
              color: userFilter === value ? '#5F2FD1' : 'rgba(27,37,80,0.617)',
            }}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {loading && <div style={{ ...orgStyles.muted, padding: '24px 4px' }}>{t('loading')}</div>}
      {!loading && users.length === 0 && unassigned.length === 0 && (
        <div style={{ ...orgStyles.card, ...orgStyles.muted, textAlign: 'center', padding: '36px' }}>
          {t('users.empty.noUsers')}
        </div>
      )}
      {!loading && users.length > 0 && visibleUsers.length === 0 && (
        <div style={{ ...orgStyles.card, ...orgStyles.muted, textAlign: 'center', padding: '36px' }}>
          {userFilter === 'reset' ? t('users.empty.noResetUsers') : t('users.empty.noUsersInView')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {visibleUsers.map((u) => renderRow(u, false))}
      </div>

      {unassigned.length > 0 && (
        <>
          <div style={{ ...orgStyles.muted, fontSize: '12px', margin: '18px 0 8px', fontWeight: 600 }}>
            {t('users.unassignedHeading')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {unassigned.map((u) => renderRow(u, true))}
          </div>
        </>
      )}
    </div>
  );
}
